import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Line } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import { Plus, X, TrendingUp } from 'lucide-react';
import { Device } from '../types';

// Use relative URL in development to leverage the proxy, absolute URL in production
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_BASE_URL || '')
  : '';

interface MetricConfig {
  key: string;
  label: string;
  unit: string;
  color: string;
  deviceTypes?: string[];
}

const AVAILABLE_METRICS: MetricConfig[] = [
  // Buoy metrics
  { key: 'temperature', label: 'Temperature', unit: '°C', color: '#ff6384', deviceTypes: ['buoy', 'netatmo_weather', 'netatmo_outdoor', 'netatmo_indoor'] },
  { key: 'significant_wave_height', label: 'Significant Wave Height', unit: 'm', color: '#36a2eb', deviceTypes: ['buoy'] },
  { key: 'maximum_wave_height', label: 'Maximum Wave Height', unit: 'm', color: '#ffce56', deviceTypes: ['buoy'] },
  { key: 'peak_wave_period', label: 'Peak Wave Period', unit: 's', color: '#4bc0c0', deviceTypes: ['buoy'] },
  { key: 'mean_wave_period', label: 'Mean Wave Period', unit: 's', color: '#9966ff', deviceTypes: ['buoy'] },
  { key: 'signal_strength', label: 'Signal Strength', unit: '', color: '#ff9f40', deviceTypes: ['buoy'] },
  { key: 'latitude', label: 'Latitude', unit: '°', color: '#c9cbcf', deviceTypes: ['buoy'] },
  { key: 'longitude', label: 'Longitude', unit: '°', color: '#8e5ea2', deviceTypes: ['buoy'] },

  // Netatmo metrics
  { key: 'humidity', label: 'Humidity', unit: '%', color: '#3e95cd', deviceTypes: ['netatmo_weather', 'netatmo_outdoor', 'netatmo_indoor'] },
  { key: 'pressure', label: 'Pressure', unit: 'mbar', color: '#8e5ea2', deviceTypes: ['netatmo_weather', 'netatmo_indoor'] },
  { key: 'co2', label: 'CO₂', unit: 'ppm', color: '#c45850', deviceTypes: ['netatmo_weather', 'netatmo_indoor'] },
  { key: 'noise', label: 'Noise', unit: 'dB', color: '#e8c3b9', deviceTypes: ['netatmo_weather', 'netatmo_indoor'] },
  { key: 'rain', label: 'Rain', unit: 'mm', color: '#3cba9f', deviceTypes: ['netatmo_rain'] },
  { key: 'wind_strength', label: 'Wind Speed', unit: 'km/h', color: '#2c3e50', deviceTypes: ['netatmo_wind'] },
  { key: 'gust_strength', label: 'Gust Speed', unit: 'km/h', color: '#34495e', deviceTypes: ['netatmo_wind'] },
  { key: 'wind_angle', label: 'Wind Direction', unit: '°', color: '#7f8c8d', deviceTypes: ['netatmo_wind'] },
];

interface SelectedMetric {
  deviceId: string;
  deviceName: string;
  metricKey: string;
  metricLabel: string;
  color: string;
}

const ComparisonPage: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<SelectedMetric[]>([]);
  const [chartData, setChartData] = useState<any>(null);
  const [timeRange, setTimeRange] = useState<'1h' | '1d' | '1w' | '1m' | 'custom'>('1d');
  const [isAddingMetric, setIsAddingMetric] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [selectedMetric, setSelectedMetric] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  useEffect(() => {
    fetchDevices();
  }, []);

  useEffect(() => {
    if (selectedMetrics.length > 0) {
      console.log('useEffect triggered - calling fetchComparisonData');
      fetchComparisonData();
    } else {
      console.log('useEffect triggered - no metrics selected, clearing chart');
      setChartData(null);
    }
  }, [selectedMetrics, timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDevices = async () => {
    setLoadingDevices(true);
    setDevicesError(null);
    try {
      console.log('Fetching devices from:', `${API_BASE_URL}/api/devices?limit=1000`);
      const response = await fetch(`${API_BASE_URL}/api/devices?limit=1000`);

      if (!response.ok) {
        throw new Error(`Failed to fetch devices: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Devices response:', data);

      // Flatten devices to include modules
      const allDevices: Device[] = [];
      data.devices.forEach((device: Device) => {
        if (device.isStation && device.modules) {
          device.modules.forEach(module => allDevices.push(module));
        } else if (!device.isStation) {
          allDevices.push(device);
        }
      });

      console.log('Processed devices:', allDevices);
      setDevices(allDevices);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setDevicesError(error instanceof Error ? error.message : 'Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchComparisonData = async () => {
    setLoading(true);
    console.log('=== Fetching Comparison Data ===');
    console.log('Selected metrics:', selectedMetrics);
    console.log('Time range:', timeRange);

    try {
      const datasets = await Promise.all(
        selectedMetrics.map(async (metric) => {
          const url = `${API_BASE_URL}/api/devices/${metric.deviceId}/messages?timeRange=${timeRange}&limit=1000`;
          console.log(`Fetching data for ${metric.deviceName} - ${metric.metricLabel}`);
          console.log('URL:', url);
          console.log('Metric key:', metric.metricKey);

          const response = await fetch(url);

          if (!response.ok) {
            console.error(`Failed to fetch data for device ${metric.deviceId}:`, response.statusText);
            return {
              label: `${metric.deviceName} - ${metric.metricLabel}`,
              data: [],
              borderColor: metric.color,
              backgroundColor: metric.color + '33',
              tension: 0.4,
              pointRadius: 2,
              pointHoverRadius: 5,
              fill: false,
            };
          }

          const data = await response.json();
          console.log(`Response data for ${metric.deviceName}:`, data);

          // Backend returns { messages: [...], total, page, limit, totalPages }
          const messages = Array.isArray(data) ? data : (data.messages || []);
          console.log('Number of messages:', messages.length);

          if (messages.length === 0) {
            console.warn(`No messages found for device ${metric.deviceId}`);
            return {
              label: `${metric.deviceName} - ${metric.metricLabel}`,
              data: [],
              borderColor: metric.color,
              backgroundColor: metric.color + '33',
              tension: 0.4,
              pointRadius: 2,
              pointHoverRadius: 5,
              fill: false,
            };
          }

          // Log the first message to see its structure
          if (messages.length > 0) {
            console.log('First message structure:', messages[0]);
            console.log('First message decodedJson:', messages[0].decodedJson);
          }

          const points = messages
            .map((msg: any) => {
              const value = msg.decodedJson[metric.metricKey];

              if (value === undefined || value === null || value === -300) {
                console.log(`Skipping value for ${metric.metricKey}:`, value);
                return null;
              }

              const parsedValue = parseFloat(value);
              if (isNaN(parsedValue)) {
                console.warn(`Invalid value for ${metric.metricKey}:`, value);
                return null;
              }

              return {
                x: new Date(msg.createdAt).getTime(),
                y: parsedValue,
              };
            })
            .filter((p: any) => p !== null);

          console.log(`Processed ${points.length} valid points for ${metric.deviceName} - ${metric.metricLabel}`);
          if (points.length > 0) {
            console.log('Sample points:', points.slice(0, 3));
          }

          return {
            label: `${metric.deviceName} - ${metric.metricLabel}`,
            data: points,
            borderColor: metric.color,
            backgroundColor: metric.color + '33',
            tension: 0.4,
            pointRadius: 2,
            pointHoverRadius: 5,
            fill: false,
          };
        })
      );

      const validDatasets = datasets.filter(ds => ds.data.length > 0);
      console.log('Valid datasets with data:', validDatasets.length);
      console.log('Total datasets:', datasets.length);

      const chartDataToSet = {
        datasets: validDatasets,
      };

      console.log('Setting chart data:', chartDataToSet);
      setChartData(chartDataToSet);
    } catch (error) {
      console.error('Error fetching comparison data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addMetric = () => {
    if (!selectedDevice || !selectedMetric) return;

    const device = devices.find(d => String(d.id) === String(selectedDevice));
    const metric = AVAILABLE_METRICS.find(m => m.key === selectedMetric);

    if (!device || !metric) {
      console.log('Failed to add metric - device or metric not found');
      console.log('Selected device ID:', selectedDevice);
      console.log('Selected metric key:', selectedMetric);
      console.log('Device found:', device);
      console.log('Metric found:', metric);
      return;
    }

    console.log('Adding metric:', { device, metric });

    const color = getNextColor();
    setSelectedMetrics([
      ...selectedMetrics,
      {
        deviceId: String(device.id), // Ensure deviceId is a string
        deviceName: device.name || device.deviceId,
        metricKey: metric.key,
        metricLabel: metric.label,
        color,
      },
    ]);

    setIsAddingMetric(false);
    setSelectedDevice('');
    setSelectedMetric('');
  };

  const removeMetric = (index: number) => {
    setSelectedMetrics(selectedMetrics.filter((_, i) => i !== index));
  };

  const getNextColor = () => {
    const colors = ['#ff6384', '#36a2eb', '#ffce56', '#4bc0c0', '#9966ff', '#ff9f40', '#c9cbcf', '#8e5ea2'];
    return colors[selectedMetrics.length % colors.length];
  };

  const getAvailableMetricsForDevice = (deviceId: string) => {
    const device = devices.find(d => String(d.id) === String(deviceId));
    if (!device) {
      console.log('Device not found for ID:', deviceId);
      console.log('Available devices:', devices.map(d => ({ id: d.id, name: d.name })));
      return [];
    }

    console.log('Selected device:', device);
    console.log('Device type:', device.deviceType);

    const availableMetrics = AVAILABLE_METRICS.filter(metric => {
      // If no device types specified, metric is available for all devices
      if (!metric.deviceTypes || metric.deviceTypes.length === 0) {
        return true;
      }
      // Check if device type matches any of the metric's device types
      const matches = metric.deviceTypes.includes(device.deviceType || 'buoy');
      console.log(`Metric ${metric.label}: deviceTypes=${metric.deviceTypes}, matches=${matches}`);
      return matches;
    });

    console.log('Available metrics for device:', availableMetrics);
    return availableMetrics;
  };

  const getTimeUnit = () => {
    switch (timeRange) {
      case '1h':
        return 'minute';
      case '1d':
        return 'hour';
      case '1w':
        return 'day';
      case '1m':
        return 'day';
      case 'custom':
        // Calculate based on date range
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
          if (diffDays <= 1) return 'hour';
          if (diffDays <= 7) return 'day';
          return 'day';
        }
        return 'day';
      default:
        return 'hour';
    }
  };

  const getTimeRange = () => {
    const now = new Date();
    switch (timeRange) {
      case '1h':
        return {
          min: new Date(now.getTime() - 60 * 60 * 1000).getTime(),
          max: now.getTime(),
        };
      case '1d':
        return {
          min: new Date(now.getTime() - 24 * 60 * 60 * 1000).getTime(),
          max: now.getTime(),
        };
      case '1w':
        return {
          min: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).getTime(),
          max: now.getTime(),
        };
      case '1m':
        return {
          min: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).getTime(),
          max: now.getTime(),
        };
      case 'custom':
        if (customStartDate && customEndDate) {
          return {
            min: new Date(customStartDate).getTime(),
            max: new Date(customEndDate).getTime(),
          };
        }
        return {
          min: new Date(now.getTime() - 24 * 60 * 60 * 1000).getTime(),
          max: now.getTime(),
        };
      default:
        return {
          min: new Date(now.getTime() - 24 * 60 * 60 * 1000).getTime(),
          max: now.getTime(),
        };
    }
  };

  const chartOptions: ChartOptions<'line'> = useMemo(() => {
    const timeRangeBounds = getTimeRange();

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: '#e0e0e0',
            padding: 15,
            font: { size: 12 },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#444',
          borderWidth: 1,
        },
      },
      scales: {
        x: {
          type: 'time',
          min: timeRangeBounds.min,
          max: timeRangeBounds.max,
          time: {
            unit: getTimeUnit() as any,
            displayFormats: {
              minute: 'HH:mm',
              hour: 'MMM d, HH:mm',
              day: 'MMM d',
              week: 'MMM d',
              month: 'MMM yyyy',
            },
          },
          grid: {
            color: '#2a2a2a',
          },
          ticks: {
            color: '#888',
            maxRotation: 0,
            autoSkipPadding: 20,
          },
        },
        y: {
          grid: {
            color: '#2a2a2a',
          },
          ticks: {
            color: '#888',
          },
        },
      },
    };
  }, [timeRange, customStartDate, customEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container>
      <Header>
        <Title>
          <TrendingUp size={28} />
          Metric Comparison
        </Title>
        <Subtitle>Compare metrics across multiple devices</Subtitle>
      </Header>

      <ControlsSection>
        <MetricsPanel>
          <PanelHeader>
            <PanelTitle>Selected Metrics ({selectedMetrics.length})</PanelTitle>
            <AddButton onClick={() => setIsAddingMetric(true)}>
              <Plus size={16} />
              Add Metric
            </AddButton>
          </PanelHeader>

          {selectedMetrics.length === 0 && !isAddingMetric && (
            <EmptyState>
              <Plus size={48} />
              <p>No metrics selected</p>
              <small>Click "Add Metric" to start comparing</small>
            </EmptyState>
          )}

          <MetricsList>
            {selectedMetrics.map((metric, index) => (
              <MetricChip key={index}>
                <ColorDot style={{ backgroundColor: metric.color }} />
                <MetricInfo>
                  <DeviceName>{metric.deviceName}</DeviceName>
                  <MetricName>{metric.metricLabel}</MetricName>
                </MetricInfo>
                <RemoveButton onClick={() => removeMetric(index)}>
                  <X size={16} />
                </RemoveButton>
              </MetricChip>
            ))}

            {isAddingMetric && (
              <AddMetricForm>
                {devicesError && (
                  <ErrorMessage>{devicesError}</ErrorMessage>
                )}
                {loadingDevices && (
                  <LoadingMessage>Loading devices...</LoadingMessage>
                )}
                <FormGroup>
                  <Label>Device ({devices.length} available)</Label>
                  <Select
                    value={selectedDevice}
                    onChange={(e) => {
                      console.log('Device selected:', e.target.value);
                      setSelectedDevice(e.target.value);
                      setSelectedMetric('');
                    }}
                  >
                    <option value="">Select a device...</option>
                    {devices.map((device) => (
                      <option key={device.id} value={device.id}>
                        {device.name || device.deviceId} ({device.deviceType})
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>
                    Metric
                    {selectedDevice && ` (${getAvailableMetricsForDevice(selectedDevice).length} available)`}
                  </Label>
                  <Select
                    value={selectedMetric}
                    onChange={(e) => {
                      console.log('Metric selected:', e.target.value);
                      setSelectedMetric(e.target.value);
                    }}
                    disabled={!selectedDevice}
                  >
                    <option value="">Select a metric...</option>
                    {selectedDevice &&
                      getAvailableMetricsForDevice(selectedDevice).map((metric) => (
                        <option key={metric.key} value={metric.key}>
                          {metric.label} ({metric.unit})
                        </option>
                      ))}
                  </Select>
                </FormGroup>

                <FormActions>
                  <CancelButton onClick={() => setIsAddingMetric(false)}>
                    Cancel
                  </CancelButton>
                  <ConfirmButton
                    onClick={addMetric}
                    disabled={!selectedDevice || !selectedMetric}
                  >
                    Add
                  </ConfirmButton>
                </FormActions>
              </AddMetricForm>
            )}
          </MetricsList>
        </MetricsPanel>

        <TimeRangePanel>
          <PanelTitle>Time Range</PanelTitle>
          <TimeRangeButtons>
            <TimeButton
              $active={timeRange === '1h'}
              onClick={() => setTimeRange('1h')}
            >
              1 Hour
            </TimeButton>
            <TimeButton
              $active={timeRange === '1d'}
              onClick={() => setTimeRange('1d')}
            >
              1 Day
            </TimeButton>
            <TimeButton
              $active={timeRange === '1w'}
              onClick={() => setTimeRange('1w')}
            >
              1 Week
            </TimeButton>
            <TimeButton
              $active={timeRange === '1m'}
              onClick={() => setTimeRange('1m')}
            >
              1 Month
            </TimeButton>
            <TimeButton
              $active={timeRange === 'custom'}
              onClick={() => setTimeRange('custom')}
            >
              Custom Range
            </TimeButton>
          </TimeRangeButtons>

          {timeRange === 'custom' && (
            <CustomDateRange>
              <DateInputGroup>
                <DateLabel>Start Date</DateLabel>
                <DateInput
                  type="datetime-local"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </DateInputGroup>
              <DateInputGroup>
                <DateLabel>End Date</DateLabel>
                <DateInput
                  type="datetime-local"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </DateInputGroup>
              <ApplyButton
                onClick={() => {
                  if (customStartDate && customEndDate && selectedMetrics.length > 0) {
                    fetchComparisonData();
                  }
                }}
                disabled={!customStartDate || !customEndDate}
              >
                Apply
              </ApplyButton>
            </CustomDateRange>
          )}
        </TimeRangePanel>
      </ControlsSection>

      <ChartSection>
        {loading ? (
          <LoadingState>Loading data...</LoadingState>
        ) : selectedMetrics.length === 0 ? (
          <EmptyChartState>
            <TrendingUp size={64} />
            <p>Select metrics to display comparison chart</p>
          </EmptyChartState>
        ) : !chartData || chartData.datasets.length === 0 ? (
          <EmptyChartState>
            <p>No data available for selected metrics</p>
          </EmptyChartState>
        ) : (
          <ChartContainer>
            <Line data={chartData} options={chartOptions} />
          </ChartContainer>
        )}
      </ChartSection>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 30px;
  background: #0a0f1f;
  min-height: calc(100vh - 60px);
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #e0e0e0;
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #1d388d;
  }
`;

const Subtitle = styled.p`
  color: #888;
  font-size: 14px;
  margin: 0;
`;

const ControlsSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MetricsPanel = styled.div`
  background: #1a1a2e;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #2a2a3e;
`;

const TimeRangePanel = styled.div`
  background: #1a1a2e;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #2a2a3e;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const PanelTitle = styled.h3`
  color: #e0e0e0;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px 0;
`;

const AddButton = styled.button`
  background: #1d388d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: #2648b3;
    transform: translateY(-1px);
  }
`;

const MetricsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MetricChip = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #0f1419;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #2a2a3e;
`;

const ColorDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const MetricInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DeviceName = styled.div`
  color: #888;
  font-size: 12px;
  margin-bottom: 2px;
`;

const MetricName = styled.div`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 500;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #ff4444;
    color: white;
  }
`;

const AddMetricForm = styled.div`
  background: #0f1419;
  padding: 16px;
  border-radius: 8px;
  border: 2px dashed #2a2a3e;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  color: #e0e0e0;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  background: #1a1a2e;
  border: 1px solid #2a2a3e;
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  option {
    background: #1a1a2e;
    color: #e0e0e0;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 10px;
  background: transparent;
  border: 1px solid #2a2a3e;
  border-radius: 6px;
  color: #888;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #1a1a2e;
    color: #e0e0e0;
  }
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 10px;
  background: #1d388d;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #2648b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TimeRangeButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TimeButton = styled.button<{ $active: boolean }>`
  padding: 12px 16px;
  background: ${props => (props.$active ? '#1d388d' : 'transparent')};
  border: 1px solid ${props => (props.$active ? '#1d388d' : '#2a2a3e')};
  border-radius: 6px;
  color: ${props => (props.$active ? 'white' : '#888')};
  font-size: 14px;
  font-weight: ${props => (props.$active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => (props.$active ? '#2648b3' : '#1a1a2e')};
    color: white;
    border-color: ${props => (props.$active ? '#2648b3' : '#3a3a4e')};
  }
`;

const ChartSection = styled.div`
  background: #1a1a2e;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #2a2a3e;
  min-height: 500px;
`;

const ChartContainer = styled.div`
  height: 500px;
  position: relative;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
  text-align: center;

  svg {
    margin-bottom: 16px;
    opacity: 0.5;
  }

  p {
    margin: 0 0 8px 0;
    font-size: 16px;
    color: #888;
  }

  small {
    font-size: 13px;
    color: #666;
  }
`;

const EmptyChartState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 500px;
  color: #666;

  svg {
    margin-bottom: 16px;
    opacity: 0.3;
  }

  p {
    margin: 0;
    font-size: 16px;
    color: #888;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 500px;
  color: #888;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  background: #1f1f2e;
  border: 1px solid #ff4444;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const LoadingMessage = styled.div`
  color: #888;
  font-size: 14px;
  margin-bottom: 16px;
`;

const CustomDateRange = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DateLabel = styled.label`
  color: #e0e0e0;
  font-size: 13px;
  font-weight: 500;
`;

const DateInput = styled.input`
  padding: 10px;
  background: #1a1a2e;
  border: 1px solid #2a2a3e;
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 14px;
  width: 100%;

  &::placeholder {
    color: #666;
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(0.8);
    cursor: pointer;
  }
`;

const ApplyButton = styled.button`
  padding: 10px;
  background: #1d388d;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover:not(:disabled) {
    background: #2648b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default ComparisonPage;

